import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { noOp } from '@neslotech/utils';

import { useAppDispatch, useAppSelector } from '../hooks';

import { TombstoneActions } from '../actions/tombstone/tombstone.actions';
import { ClientActions } from '../actions/client/client.actions';
import { CategoryActions } from '../actions/category/category.actions';

import type { Tombstone } from '../interfaces/client/tombstone.interface';
import type { Client } from '../interfaces/client/client.interface';
import type { Category } from '../interfaces/config/category.interface';

import Modal from './components/Modal';
import { num, formatZar } from './lib/mandate-helpers';

// =============================================================================
//  Create tombstone modal (v3)
//
//  Minimum-viable v3 capture flow for a closed-deal entry.  Required by
//  the backend Tombstone model:
//    - client_id  (NOT NULL FK; supplied in the body for v3, set via URL
//                  in the legacy /clients/:client_id/tombstones path)
//    - category_id
//    - date
//
//  Optional but useful at creation: bsm_role, deal_type, sub_category,
//  size / fee, free-text description.  `linked_people` (engagement team)
//  is left for post-create edit since it needs the employee picker UI
//  we haven't built into a modal yet.
//
//  Save dispatches CREATE_TOMBSTONE → POST /tombstones, refreshes the
//  global tombstone store, and navigates to the new tombstone's detail
//  page on success.
// =============================================================================

const DEAL_TYPES = [
  'M&A',
  'Listing',
  'Capital Raise',
  'Disposal',
  'Restructuring',
  'Business Rescue Processes',
  'Black Economic Empowerment Transactions',
  'Independent Business Review',
  'Strategic Advice',
  'Valuation',
  'Research'
] as const;

const BSM_ROLES = [
  'Sell-side advisor',
  'Buy-side advisor',
  'Lead advisor',
  'Independent advisor',
  'Sole financial advisor',
  'JSE Sponsor'
] as const;

interface Props {
  onClose: () => void;
  /** When provided, prefills the client picker (e.g. from /clients/:id). */
  defaultClientId?: number | string;
}

const todayIso = (): string => new Date().toISOString().slice(0, 10);

const CreateTombstoneModalV3 = ({ onClose, defaultClientId }: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const clients: Client[] = useAppSelector(({ client_store }) => client_store.clients) || [];
  const categories: Category[] =
    useAppSelector(({ category_store }) => category_store.categories) || [];

  useEffect(() => {
    if (clients.length === 0) dispatch(ClientActions.loadClients(noOp));
    if (categories.length === 0) dispatch(CategoryActions.loadCategories(noOp));
  }, [dispatch, clients.length, categories.length]);

  const [clientId, setClientId] = useState<string>(defaultClientId ? String(defaultClientId) : '');
  const [categoryId, setCategoryId] = useState<string>('');
  const [date, setDate] = useState<string>(todayIso());
  const [dealType, setDealType] = useState<string>('');
  const [bsmRole, setBsmRole] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [sizeOfTransaction, setSizeOfTransaction] = useState<string>('');
  const [feeOfTransaction, setFeeOfTransaction] = useState<string>('');

  const [saving, setSaving] = useState(false);

  const requiredOk = clientId !== '' && categoryId !== '' && date !== '';

  const handleSave = () => {
    if (!requiredOk) return;
    setSaving(true);
    dispatch(
      TombstoneActions.createTombstone(
        {
          client_id: Number(clientId),
          category_id: Number(categoryId),
          date,
          description: description || undefined,
          size_of_transaction: sizeOfTransaction === '' ? 0 : Number(sizeOfTransaction),
          fee_of_transaction: feeOfTransaction === '' ? 0 : Number(feeOfTransaction),
          ...(bsmRole ? { bsm_role: bsmRole } : {}),
          ...(dealType ? { deal_type: dealType } : {})
        } as Partial<Tombstone>,
        (created?: Tombstone) => {
          setSaving(false);
          onClose();
          if (created?.id) navigate(`/tombstones/${created.id}`);
        }
      )
    );
  };

  const footer = (
    <>
      <button type="button" className="btn ghost" onClick={onClose} disabled={saving}>
        Cancel
      </button>
      <button
        type="button"
        className="btn primary"
        onClick={handleSave}
        disabled={saving || !requiredOk}
      >
        {saving ? 'Creating…' : 'Create tombstone'}
      </button>
    </>
  );

  return (
    <Modal title="New tombstone" onClose={onClose} footer={footer} wide>
      <section className="v3-modal-section">
        <h4 className="section-title">Deal</h4>

        <div className="field row-2">
          <div className="field" style={{ gap: 6 }}>
            <span className="label">Client *</span>
            <select value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">— Select a client —</option>
              {clients.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field" style={{ gap: 6 }}>
            <span className="label">Category *</span>
            <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
              <option value="">— Select a category —</option>
              {categories.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="field row-2">
          <div className="field" style={{ gap: 6 }}>
            <span className="label">Deal type</span>
            <input
              type="text"
              list="v3-create-tombstone-deal-types"
              value={dealType}
              onChange={(e) => setDealType(e.target.value)}
              placeholder="e.g. M&A"
            />
            <datalist id="v3-create-tombstone-deal-types">
              {DEAL_TYPES.map((t) => (
                <option key={t} value={t} />
              ))}
            </datalist>
          </div>
          <div className="field" style={{ gap: 6 }}>
            <span className="label">BSM role</span>
            <input
              type="text"
              list="v3-create-tombstone-roles"
              value={bsmRole}
              onChange={(e) => setBsmRole(e.target.value)}
              placeholder="e.g. Sell-side advisor"
            />
            <datalist id="v3-create-tombstone-roles">
              {BSM_ROLES.map((r) => (
                <option key={r} value={r} />
              ))}
            </datalist>
          </div>
        </div>

        <div className="field">
          <span className="label">Description</span>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Short summary (e.g. 'Disposal of non-core property portfolio')"
          />
        </div>
      </section>

      <section className="v3-modal-section">
        <h4 className="section-title">Money & date</h4>

        <div className="field">
          <span className="label">Transaction size (ZAR)</span>
          <input
            type="number"
            min={0}
            step={1000}
            value={sizeOfTransaction}
            onChange={(e) => setSizeOfTransaction(e.target.value)}
            placeholder="e.g. 1800000000"
          />
          {sizeOfTransaction !== '' && (
            <span
              className="muted"
              style={{ fontSize: 11.5, marginTop: 2, textTransform: 'none', letterSpacing: 0 }}
            >
              Displays as {formatZar(num(sizeOfTransaction))}.
            </span>
          )}
        </div>

        <div className="field row-2">
          <div className="field" style={{ gap: 6 }}>
            <span className="label">Fee of transaction (ZAR)</span>
            <input
              type="number"
              min={0}
              step={1000}
              value={feeOfTransaction}
              onChange={(e) => setFeeOfTransaction(e.target.value)}
              placeholder="optional"
            />
          </div>
          <div className="field" style={{ gap: 6 }}>
            <span className="label">Date *</span>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
        </div>
      </section>

      <p className="muted" style={{ fontSize: 12, lineHeight: 1.4, marginTop: 4, marginBottom: 0 }}>
        Linked people (engagement team) can be added after create via the tombstone detail page or
        the legacy form at <code>/clients/:id/tombstones</code>.
      </p>
    </Modal>
  );
};

export default CreateTombstoneModalV3;
